import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AuthGate } from '../features/auth/auth-gate.js';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { authFetch } from '../features/auth/auth-fetch.js';
import { HOTELS_QUERY_KEY, listHotels } from '../features/hotels/api.js';
import { UsageSummaryCards } from '../features/usage/UsageSummaryCards.js';
import { UsageBucketChart } from '../features/usage/UsageBucketChart.js';
import { UsageByModelTable } from '../features/usage/UsageByModelTable.js';
import { UsageByHotelTable } from '../features/usage/UsageByHotelTable.js';
import { QueueHealthCard } from '../features/usage/QueueHealthCard.js';
import { BudgetCard } from '../features/usage/BudgetCard.js';
import type {
  TokenUsageSummary,
  UsageBucketGranularity,
  UsageFilters,
} from '../features/usage/types.js';

interface HotelOption {
  id: string;
  name: string;
}

function defaultFromIso(): string {
  // Default range: last 30 days.
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultToIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoToDateTimeStart(date: string): string {
  return new Date(`${date}T00:00:00.000Z`).toISOString();
}

function isoToDateTimeEnd(date: string): string {
  return new Date(`${date}T23:59:59.999Z`).toISOString();
}

function buildQuery(filters: UsageFilters): string {
  const params = new URLSearchParams();
  params.set('bucket', filters.bucket);
  if (filters.hotelId) params.set('hotelId', filters.hotelId);
  if (filters.from) params.set('from', isoToDateTimeStart(filters.from));
  if (filters.to) params.set('to', isoToDateTimeEnd(filters.to));
  return params.toString();
}

function AdminUsageInner() {
  const [filters, setFilters] = useState<UsageFilters>({
    bucket: 'day',
    hotelId: null,
    from: defaultFromIso(),
    to: defaultToIso(),
  });
  const [draft, setDraft] = useState<UsageFilters>(filters);

  // Shared key from features/hotels/api — same cache the rest of the app uses,
  // so the /hotels request is served from cache when the user navigates here
  // from the Tervek page or the dashboard. `select` narrows the shared full
  // Hotel list down to the {id, name} shape this dropdown actually needs.
  const hotelsQuery = useQuery<HotelOption[]>({
    queryKey: HOTELS_QUERY_KEY,
    queryFn: listHotels,
    select: (hotels) => hotels.map((h) => ({ id: h.id, name: h.name })),
  });

  const queryString = useMemo(() => buildQuery(filters), [filters]);

  const summaryQuery = useQuery<TokenUsageSummary>({
    queryKey: ['admin', 'usage', queryString],
    queryFn: async () => {
      const res = await authFetch(`/admin/usage?${queryString}`);
      if (!res.ok) throw new Error('failed');
      return (await res.json()) as TokenUsageSummary;
    },
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BudgetCard canEdit />
        <QueueHealthCard />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Token költések áttekintése</CardTitle>
          <CardDescription>
            AI token-felhasználás és költség lebontás modellek, hotelek és időszak szerint.
          </CardDescription>
        </CardHeader>
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters(draft);
          }}
        >
          <div>
            <Label htmlFor="usage-bucket">Bucket</Label>
            <select
              id="usage-bucket"
              value={draft.bucket}
              onChange={(e) =>
                setDraft({ ...draft, bucket: e.target.value as UsageBucketGranularity })
              }
              className="flex h-10 w-full rounded-md border border-[var(--app-divider-2)] bg-[var(--app-surface)] px-3 text-body"
            >
              <option value="day">Nap</option>
              <option value="week">Hét</option>
              <option value="month">Hónap</option>
            </select>
          </div>
          <div>
            <Label htmlFor="usage-hotel">Hotel</Label>
            <select
              id="usage-hotel"
              value={draft.hotelId ?? ''}
              onChange={(e) => setDraft({ ...draft, hotelId: e.target.value || null })}
              className="flex h-10 w-full rounded-md border border-[var(--app-divider-2)] bg-[var(--app-surface)] px-3 text-body"
            >
              <option value="">Összes hotel</option>
              {hotelsQuery.data?.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="usage-from">Kezdő dátum</Label>
            <Input
              id="usage-from"
              type="date"
              value={draft.from ?? ''}
              onChange={(e) => setDraft({ ...draft, from: e.target.value || null })}
            />
          </div>
          <div>
            <Label htmlFor="usage-to">Záró dátum</Label>
            <Input
              id="usage-to"
              type="date"
              value={draft.to ?? ''}
              onChange={(e) => setDraft({ ...draft, to: e.target.value || null })}
            />
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={summaryQuery.isFetching} className="w-full">
              {summaryQuery.isFetching ? 'Frissítés…' : 'Frissítés'}
            </Button>
          </div>
        </form>
      </Card>

      {summaryQuery.isLoading && (
        <Card>
          <p className="text-center text-body text-[var(--fg-muted)]">Betöltés…</p>
        </Card>
      )}
      {summaryQuery.error && (
        <Card>
          <p className="text-center text-body text-red-700">Nem sikerült betölteni az adatokat.</p>
        </Card>
      )}

      {summaryQuery.data && (
        <>
          <UsageSummaryCards summary={summaryQuery.data} />
          <UsageBucketChart buckets={summaryQuery.data.buckets} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <UsageByModelTable rows={summaryQuery.data.byModel} />
            <UsageByHotelTable rows={summaryQuery.data.byHotel} />
          </div>
        </>
      )}
    </div>
  );
}

function AdminUsagePage() {
  return (
    <AuthGate roles={['admin']}>
      <AdminUsageInner />
    </AuthGate>
  );
}

export const Route = createFileRoute('/app/admin/usage')({
  component: AdminUsagePage,
});
